import styled from 'foundations-theming/utils';
import useDebouncedEditorStateContext from 'hubspot-prosemirror/prosemirror/contexts/useDebouncedEditorStateContext';
import { Popover } from 'foundations-components/transitional/components/Popover';
import FormattedHTMLMessage from 'I18n/components/FormattedHTMLMessage';
import FormattedMessage from 'I18n/components/FormattedMessage';
import I18n from 'I18n';
import { useEffect, useState, useContext } from 'react';
import ContentAssistantInlineAutocompleteMenu from './CAAutocompleteMenu';
import { getDisabledTooltip, initContentAssistantSlashPluginState } from './utils';
import usePrevious from 'react-utils/hooks/usePrevious';
import { LoadingSpinner } from 'foundations-components/transitional/components/LoadingSpinner';
import { useSummaryCommand } from '../../commands/ai/useSummaryCommand';
import { EditorConfigContext } from '../../contexts/EditorConfigContext';
import { Tooltip } from 'foundations-components/transitional/components/Tooltip';
import { jsx as _jsx } from "react/jsx-runtime";
const DISABLED_TEXT_COLOR = '#b0c1d4';
const OptionsUl = styled.ul.withConfig({
  displayName: "CADropdown__OptionsUl",
  componentId: "sc-1ri5ytj-0"
})(["-webkit-tap-highlight-color:rgba(0,0,0,0);border-radius:inherit;list-style:none;margin:", ";max-height:220px;overflow-x:hidden;overflow-y:auto;padding:", ";position:relative;"], ({
  theme
}) => theme.space['0'], ({
  theme
}) => theme.space['0']);
const OptionsLi = styled.li.withConfig({
  displayName: "CADropdown__OptionsLi",
  componentId: "sc-1ri5ytj-1"
})(["padding:", ";&.focus{background-color:", ";}"], ({
  theme
}) => `${theme.space['200']} ${theme.space['300']}`, ({
  theme
}) => theme.color['fill-surface-default-hover']);
const OptionButton = styled.button.withConfig({
  displayName: "CADropdown__OptionButton",
  componentId: "sc-1ri5ytj-2"
})(["background:none;border:", ";color:inherit;padding:", ";text-align:inherit;text-transform:inherit;"], ({
  theme
}) => theme.borderWidth['0'], ({
  theme
}) => theme.space['0']);
export default function ContentAssistantDropdown({
  view,
  options,
  pluginKey,
  onInsert,
  getSelectedCommandParams,
  usageDescription
}) {
  const {
    editorState
  } = useDebouncedEditorStateContext();
  const pluginState = pluginKey.getState(editorState) || {};
  const {
    searchTerm,
    currentDropdownIndex,
    popoverActive
  } = pluginState;
  const displayOptions = searchTerm ? Object.keys(options).filter(key => {
    return I18n.text(options[key].labelKey).toLowerCase().indexOf(searchTerm.toLowerCase()) > -1;
  }) : Object.keys(options);
  const {
    AI_ASSISTANT: {
      globalSummarizationEnabled = false,
      summarizationDisabledMessage = undefined
    } = {},
    CONTENT_ASSISTANT_SLASH_COMMAND: {
      onSelectContentAssistantOption = undefined,
      trackOpenPopover = undefined
    } = {}
  } = useContext(EditorConfigContext);
  useEffect(() => {
    if (trackOpenPopover) {
      trackOpenPopover();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const [selectedOption, setSelectedOption] = useState(null);
  const {
    isLoading: isGeneratingSummary,
    generateSummary
  } = useSummaryCommand();

  // since the browser focus is still on the editor so user can continue to type
  // have to listen to on Enter event while dropdown is open
  useEffect(() => {
    const keyupListener = e => {
      if (e.key === 'Enter') {
        const optionId = displayOptions[currentDropdownIndex];
        if (onSelectContentAssistantOption) {
          onSelectContentAssistantOption({
            selected: optionId,
            command: options[optionId].id
          });
        }
        switch (optionId) {
          case 'generate_summary':
            return globalSummarizationEnabled && generateSummary();
          default:
            return setSelectedOption(options[optionId]);
        }
      }
    };
    window.addEventListener('keyup', keyupListener);
    return () => {
      window.removeEventListener('keyup', keyupListener);
    };
  }, [currentDropdownIndex, selectedOption, options, displayOptions, generateSummary, globalSummarizationEnabled, onSelectContentAssistantOption]);
  const onSelectCommand = selectedCommand => {
    if (onSelectContentAssistantOption && selectedCommand.id) {
      onSelectContentAssistantOption({
        selected: selectedCommand.id,
        command: selectedCommand.id
      });
    }
    switch (selectedCommand.id) {
      case 'generate_summary':
        return generateSummary();
      default:
        view.dispatch(view.state.tr.setMeta(pluginKey, Object.assign({}, pluginState, {
          popoverActive: true
        })));
        return setSelectedOption(selectedCommand);
    }
  };
  const handleOnMenuClose = () => {
    view.dispatch(view.state.tr.setMeta(pluginKey, initContentAssistantSlashPluginState()));
  };
  const prevLoading = usePrevious(isGeneratingSummary);
  useEffect(() => {
    if (prevLoading && !isGeneratingSummary) {
      view.dispatch(view.state.tr.delete(pluginState.searchTextStartPos, pluginState.searchTextStartPos + 1).setMeta(pluginKey, initContentAssistantSlashPluginState()));
    }
  }, [prevLoading, isGeneratingSummary, pluginState.searchTextStartPos, pluginKey, view]);
  const dropdownContent =
  /*#__PURE__*/
  // @bend-expect trellis-tokenized
  _jsx(OptionsUl, {
    "data-test-id": "content-assistant-slash-dropdown-list",
    children: displayOptions.length ? displayOptions.map((key, index) => {
      const disabledTooltip = getDisabledTooltip(options[key], globalSummarizationEnabled, summarizationDisabledMessage);
      const optionButton =
      /*#__PURE__*/
      // @bend-expect trellis-tokenized
      _jsx(OptionButton, {
        className: 'truncate-text text-center',
        onClick: onSelectCommand.bind(null, options[key]),
        disabled: !!disabledTooltip,
        style: {
          color: disabledTooltip ? DISABLED_TEXT_COLOR : 'inherit'
        },
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: options[key].labelKey
        })
      });
      return (
        /*#__PURE__*/
        // @bend-expect trellis-tokenized
        _jsx(OptionsLi, {
          "data-test-id": `content-assistant-slash-option`,
          className: currentDropdownIndex === index ? "focus" : "",
          children: disabledTooltip ? /*#__PURE__*/_jsx(Tooltip, {
            title: disabledTooltip,
            children: optionButton
          }) : optionButton
        }, key)
      );
    }) :
    /*#__PURE__*/
    // @bend-expect trellis-tokenized
    _jsx(OptionsLi, {
      children: /*#__PURE__*/_jsx(FormattedHTMLMessage, {
        message: 'inlineAutocompleteMenu.noResult'
      })
    })
  });
  const showPopover = popoverActive && selectedOption;
  if (showPopover) {
    return /*#__PURE__*/_jsx(ContentAssistantInlineAutocompleteMenu, {
      onInsert: onInsert,
      onClose: handleOnMenuClose,
      selectedItem: selectedOption,
      view: view,
      state: pluginState,
      getSelectedCommandParams: getSelectedCommandParams,
      trackInteraction: action => {
        if (onSelectContentAssistantOption) {
          const optionId = displayOptions[currentDropdownIndex];
          onSelectContentAssistantOption({
            selected: action,
            command: options[optionId].id
          });
        }
      },
      usageDescription: usageDescription,
      pluginKey: pluginKey
    });
  }
  if (isGeneratingSummary) {
    return /*#__PURE__*/_jsx(LoadingSpinner, {
      size: "extra-small"
    });
  }
  return /*#__PURE__*/_jsx(Popover, {
    autoPlacement: true,
    content: dropdownContent,
    open: !isGeneratingSummary,
    placement: "bottom",
    "data-test-id": 'content-assistant-slash-popover',
    children: /*#__PURE__*/_jsx("span", {})
  });
}